import { useState } from "react";
import { Link } from "react-router-dom";
import { motion } from "framer-motion";
import { 
  Heart, Users, Building2, HandHeart, CheckCircle, ArrowRight,
  Phone, Mail, Download, Baby, GraduationCap, Sparkles, Target,
  IndianRupee, FileText, Shield, Award
} from "lucide-react";
import { Button } from "@/components/ui/button";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";

// Impact stats
const impactStats = [
  { number: "750+", label: "CI Surgeries Performed", icon: Award },
  { number: "₹8L", label: "Cost Per Child", icon: IndianRupee },
  { number: "100", label: "Children Need Help Annually", icon: Baby },
  { number: "100%", label: "Transparent Fund Usage", icon: Shield },
];

// Partnership tiers
const partnershipTiers = [
  {
    name: "Sponsor One Child",
    amount: "₹8,00,000",
    description: "Fund complete cochlear implant surgery, device, and rehabilitation for one child",
    benefits: [
      "Certificate of appreciation",
      "Child's progress updates",
      "Photo with the beneficiary child",
      "Mention in annual impact report",
      "Tax benefits under Section 80G"
    ],
    highlight: true
  },
  {
    name: "Partial Sponsorship",
    amount: "₹4,00,000",
    description: "Cover 50% of cochlear implant cost for a child",
    benefits: [
      "Certificate of appreciation",
      "Child's progress updates",
      "Tax benefits under Section 80G"
    ],
    highlight: false
  },
  {
    name: "Therapy Sponsor",
    amount: "₹1,50,000",
    description: "Fund post-operative speech therapy and rehabilitation",
    benefits: [
      "Certificate of appreciation",
      "Impact updates",
      "Tax benefits under Section 80G"
    ],
    highlight: false
  },
  {
    name: "Any Contribution",
    amount: "Any Amount",
    description: "Every contribution helps - pool donations help more children",
    benefits: [
      "Certificate of appreciation",
      "Tax benefits under Section 80G"
    ],
    highlight: false
  }
];

// Cost breakdown
const costBreakdown = [
  { item: "Cochlear Implant Device", cost: "₹6,50,000", note: "International quality implant" },
  { item: "Surgery & Hospital Charges", cost: "₹1,00,000", note: "Max Hospital, Saket" },
  { item: "Post-Op Therapy & Rehab", cost: "₹50,000", note: "12-18 months program" },
  { item: "Total Cost Per Child", cost: "₹8,00,000", note: "", isTotal: true },
];

// Why this matters
const whyMatters = [
  {
    icon: Baby,
    title: "1.57 Million Children",
    description: "In India aged 0-19 years have disabling hearing loss"
  },
  {
    icon: GraduationCap,
    title: "Missed Education",
    description: "Deaf children without intervention cannot attend regular schools"
  },
  {
    icon: Sparkles,
    title: "Life-Changing Solution",
    description: "CI enables deaf children to hear, speak, and live normal lives"
  },
  {
    icon: Target,
    title: "Early Intervention Critical",
    description: "Best outcomes when implanted before age 3"
  }
];

export default function CSRPage() {
  return (
    <div data-testid="csr-page">
      {/* Hero Section */}
      <section className="relative bg-gradient-to-br from-rose-50 via-amber-50 to-sky-50 py-16 md:py-24 overflow-hidden">
        <div className="container-width">
          <div className="grid lg:grid-cols-2 gap-12 items-center">
            <motion.div
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.6 }}
            >
              <div className="inline-flex items-center gap-2 bg-rose-100 px-4 py-2 rounded-full mb-6">
                <Heart className="w-4 h-4 text-rose-600" fill="currentColor" />
                <span className="text-sm font-semibold text-rose-700">CSR Partnership Opportunity</span>
              </div>

              <h1 className="text-4xl md:text-5xl lg:text-6xl font-bold text-slate-900 mb-6 leading-tight">
                Gift the Sound of<br />
                <span className="text-rose-600">Life to a Deaf Child</span>
              </h1>

              <p className="text-lg md:text-xl text-slate-600 mb-8 max-w-xl leading-relaxed">
                Your CSR contribution can fund cochlear implant surgery for children from underprivileged 
                families, giving them the gift of hearing and a chance at a normal life.
              </p>

              <div className="flex flex-wrap gap-4">
                <a href="#partner">
                  <Button size="lg" className="bg-rose-600 hover:bg-rose-700 text-white font-bold h-14 px-8">
                    <HandHeart className="w-5 h-5 mr-2" />
                    Partner With Us
                  </Button>
                </a>
                <a 
                  href="https://customer-assets.emergentagent.com/job_dr-sachdeva-ci/artifacts/rgjgoo8k_CI%20Brochure.pdf" 
                  target="_blank" 
                  rel="noopener noreferrer"
                >
                  <Button size="lg" variant="outline" className="h-14 px-8">
                    <Download className="w-5 h-5 mr-2" />
                    Download Brochure
                  </Button>
                </a>
              </div>
            </motion.div>

            <motion.div
              initial={{ opacity: 0, x: 20 }}
              animate={{ opacity: 1, x: 0 }}
              transition={{ duration: 0.6, delay: 0.2 }}
              className="relative"
            >
              <div className="relative rounded-2xl overflow-hidden shadow-2xl">
                <img
                  src="https://images.unsplash.com/photo-1516627145497-ae6968895b74?crop=entropy&cs=srgb&fm=jpg&ixid=M3w3NDk1NzZ8MHwxfHNlYXJjaHwyfHxoYXBweSUyMGluZGlhbiUyMGNoaWxkJTIwc21pbGluZ3xlbnwwfHx8fDE3NzIxODY1NTR8MA&ixlib=rb-4.1.0&q=85"
                  alt="Happy child who can hear"
                  className="w-full aspect-[4/3] object-cover"
                />
                <div className="absolute inset-0 bg-gradient-to-t from-rose-900/60 via-transparent to-transparent" />
                <div className="absolute bottom-6 left-6 right-6">
                  <p className="text-white text-xl font-semibold">
                    "The first time my child heard my voice, we all cried tears of joy."
                  </p>
                  <p className="text-white/80 text-sm mt-2">— Mother of CI recipient</p>
                </div>
              </div>
            </motion.div>
          </div>
        </div>
      </section>

      {/* Impact Stats */}
      <section className="bg-slate-900 py-12">
        <div className="container-width">
          <div className="grid grid-cols-2 md:grid-cols-4 gap-8">
            {impactStats.map((stat, i) => (
              <motion.div
                key={i}
                initial={{ opacity: 0, y: 20 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true }}
                transition={{ delay: i * 0.1 }}
                className="text-center"
              >
                <stat.icon className="w-8 h-8 text-rose-400 mx-auto mb-3" />
                <div className="text-3xl md:text-4xl font-bold text-white font-heading">{stat.number}</div>
                <div className="text-sm text-slate-400 mt-1">{stat.label}</div>
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      {/* Why This Matters */}
      <section className="section-padding bg-white">
        <div className="container-width">
          <div className="text-center mb-12">
            <h2 className="text-3xl md:text-4xl font-bold text-slate-900 mb-4">
              Why Your Support Matters
            </h2>
            <p className="text-lg text-slate-600 max-w-2xl mx-auto">
              Millions of children in India are born deaf or become deaf early in life. 
              Without intervention, they face a life of silence and isolation.
            </p>
          </div>

          <div className="grid md:grid-cols-2 lg:grid-cols-4 gap-8">
            {whyMatters.map((item, i) => (
              <motion.div
                key={i}
                initial={{ opacity: 0, y: 20 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true }}
                transition={{ delay: i * 0.1 }}
                className="text-center p-6 bg-slate-50 rounded-xl"
              >
                <div className="w-14 h-14 rounded-full bg-rose-100 flex items-center justify-center mx-auto mb-4">
                  <item.icon className="w-7 h-7 text-rose-600" />
                </div>
                <h3 className="font-bold text-slate-900 mb-2">{item.title}</h3>
                <p className="text-slate-600 text-sm">{item.description}</p>
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      {/* The Problem & Solution */}
      <section className="section-padding bg-slate-50">
        <div className="container-width">
          <div className="grid lg:grid-cols-2 gap-12 items-center">
            <div>
              <h2 className="text-3xl md:text-4xl font-bold text-slate-900 mb-6">
                The Challenge
              </h2>
              <div className="space-y-4 text-slate-600">
                <p>
                  Every year, approximately <strong>100,000 babies</strong> are born with hearing impairment in India. 
                  Many more lose hearing due to infections, accidents, or illness.
                </p>
                <p>
                  The consequences are devastating: <strong>delayed speech development</strong>, inability to attend 
                  regular schools, social isolation, and limited career opportunities.
                </p>
                <p>
                  While cochlear implants can transform these children's lives, the <strong>cost of ₹8 Lakhs per child</strong> 
                  puts it beyond reach for most families.
                </p>
              </div>

              <div className="mt-8 p-6 bg-rose-50 rounded-xl border border-rose-200">
                <h3 className="font-bold text-rose-900 mb-3">The Barrier</h3>
                <div className="flex items-center gap-4">
                  <div className="text-4xl font-bold text-rose-600">₹8L</div>
                  <div className="text-rose-800">
                    Cost of cochlear implant surgery — unaffordable for most Indian families
                  </div>
                </div>
              </div>
            </div>

            <div>
              <h2 className="text-3xl md:text-4xl font-bold text-slate-900 mb-6">
                The Solution
              </h2>
              <div className="space-y-4 text-slate-600">
                <p>
                  <strong>Cochlear implants</strong> are electronic devices surgically implanted in the inner ear 
                  that bypass damaged portions and directly stimulate the auditory nerve.
                </p>
                <p>
                  With early implantation and proper therapy, deaf children can <strong>hear, speak, attend regular 
                  schools</strong>, and lead completely normal lives.
                </p>
                <p>
                  Dr. Sanjay Sachdeva, with <strong>750+ successful CI surgeries</strong>, partners with HearClear 
                  and NGOs to identify underprivileged children who need this life-changing surgery.
                </p>
              </div>

              <div className="mt-8 p-6 bg-green-50 rounded-xl border border-green-200">
                <h3 className="font-bold text-green-900 mb-3">The Impact</h3>
                <ul className="space-y-2">
                  <li className="flex items-start gap-2">
                    <CheckCircle className="w-5 h-5 text-green-600 mt-0.5" />
                    <span className="text-green-800">Child can hear and develop normal speech</span>
                  </li>
                  <li className="flex items-start gap-2">
                    <CheckCircle className="w-5 h-5 text-green-600 mt-0.5" />
                    <span className="text-green-800">Can attend regular school with peers</span>
                  </li>
                  <li className="flex items-start gap-2">
                    <CheckCircle className="w-5 h-5 text-green-600 mt-0.5" />
                    <span className="text-green-800">Opens doors to education and career</span>
                  </li>
                </ul>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Cost Breakdown */}
      <section className="section-padding bg-white">
        <div className="container-width">
          <div className="max-w-3xl mx-auto">
            <div className="text-center mb-12">
              <h2 className="text-3xl md:text-4xl font-bold text-slate-900 mb-4">
                Transparent Cost Structure
              </h2>
              <p className="text-lg text-slate-600">
                Every rupee is accounted for. Here's exactly where your contribution goes.
              </p>
            </div>

            <div className="bg-slate-50 rounded-xl overflow-hidden">
              {costBreakdown.map((item, i) => (
                <div 
                  key={i} 
                  className={`flex items-center justify-between p-5 ${
                    item.isTotal ? 'bg-rose-600 text-white' : 'border-b border-slate-200'
                  }`}
                >
                  <div>
                    <div className={`font-semibold ${item.isTotal ? 'text-white text-lg' : 'text-slate-900'}`}>
                      {item.item}
                    </div>
                    {item.note && (
                      <div className={`text-sm ${item.isTotal ? 'text-rose-100' : 'text-slate-500'}`}>
                        {item.note}
                      </div>
                    )}
                  </div>
                  <div className={`font-bold text-xl ${item.isTotal ? 'text-white' : 'text-slate-900'}`}>
                    {item.cost}
                  </div>
                </div>
              ))}
            </div>
          </div>
        </div>
      </section>

      {/* Partnership Tiers */}
      <section className="section-padding bg-slate-50" id="partner">
        <div className="container-width">
          <div className="text-center mb-12">
            <h2 className="text-3xl md:text-4xl font-bold text-slate-900 mb-4">
              CSR Partnership Options
            </h2>
            <p className="text-lg text-slate-600 max-w-2xl mx-auto">
              Choose a partnership level that aligns with your CSR goals. 
              All contributions qualify for tax benefits under Section 80G.
            </p>
          </div>

          <div className="grid md:grid-cols-2 lg:grid-cols-4 gap-6">
            {partnershipTiers.map((tier, i) => (
              <motion.div
                key={i}
                initial={{ opacity: 0, y: 20 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true }}
                transition={{ delay: i * 0.1 }}
              >
                <Card className={`h-full ${tier.highlight ? 'border-rose-500 border-2 relative' : ''}`}>
                  {tier.highlight && (
                    <div className="absolute -top-3 left-1/2 -translate-x-1/2 bg-rose-500 text-white px-4 py-1 rounded-full text-sm font-semibold">
                      Most Impact
                    </div>
                  )}
                  <CardHeader className="pt-8">
                    <CardTitle className="text-lg">{tier.name}</CardTitle>
                    <div className="text-3xl font-bold text-rose-600 mt-2">{tier.amount}</div>
                  </CardHeader>
                  <CardContent>
                    <p className="text-slate-600 text-sm mb-4">{tier.description}</p>
                    <ul className="space-y-2">
                      {tier.benefits.map((benefit, j) => (
                        <li key={j} className="flex items-start gap-2 text-sm">
                          <CheckCircle className="w-4 h-4 text-green-500 mt-0.5 flex-shrink-0" />
                          <span className="text-slate-600">{benefit}</span>
                        </li>
                      ))}
                    </ul>
                  </CardContent>
                </Card>
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      {/* Process */}
      <section className="section-padding bg-white">
        <div className="container-width">
          <div className="text-center mb-12">
            <h2 className="text-3xl md:text-4xl font-bold text-slate-900 mb-4">
              How It Works
            </h2>
            <p className="text-lg text-slate-600 max-w-2xl mx-auto">
              A transparent, end-to-end process managed by Dr. Sachdeva's team and HearClear.
            </p>
          </div>

          <div className="grid md:grid-cols-3 gap-8 max-w-4xl mx-auto">
            <div className="relative text-center">
              <div className="w-16 h-16 rounded-full bg-rose-100 flex items-center justify-center mx-auto mb-4">
                <span className="text-2xl font-bold text-rose-600">1</span>
              </div>
              <h3 className="font-bold text-slate-900 mb-2">Identify Beneficiaries</h3>
              <p className="text-slate-600 text-sm">
                Camps, screenings, and diagnostics to identify children who need CI surgery
              </p>
            </div>

            <div className="relative text-center">
              <div className="w-16 h-16 rounded-full bg-rose-100 flex items-center justify-center mx-auto mb-4">
                <span className="text-2xl font-bold text-rose-600">2</span>
              </div>
              <h3 className="font-bold text-slate-900 mb-2">CI Surgery</h3>
              <p className="text-slate-600 text-sm">
                Cochlear implant surgery by Dr. Sanjay Sachdeva at Max Hospital
              </p>
            </div>

            <div className="relative text-center">
              <div className="w-16 h-16 rounded-full bg-rose-100 flex items-center justify-center mx-auto mb-4">
                <span className="text-2xl font-bold text-rose-600">3</span>
              </div>
              <h3 className="font-bold text-slate-900 mb-2">Rehabilitation</h3>
              <p className="text-slate-600 text-sm">
                Post-operative speech and language therapy for optimal outcomes
              </p>
            </div>
          </div>
        </div>
      </section>

      {/* Documents & Resources */}
      <section className="section-padding bg-slate-50">
        <div className="container-width">
          <div className="text-center mb-12">
            <h2 className="text-3xl md:text-4xl font-bold text-slate-900 mb-4">
              Resources & Documents
            </h2>
          </div>

          <div className="grid md:grid-cols-2 gap-6 max-w-2xl mx-auto">
            <a 
              href="https://customer-assets.emergentagent.com/job_dr-sachdeva-ci/artifacts/rgjgoo8k_CI%20Brochure.pdf"
              target="_blank"
              rel="noopener noreferrer"
              className="flex items-center gap-4 p-6 bg-white rounded-xl shadow-sm hover:shadow-md transition-shadow"
            >
              <div className="w-12 h-12 rounded-lg bg-rose-100 flex items-center justify-center flex-shrink-0">
                <FileText className="w-6 h-6 text-rose-600" />
              </div>
              <div>
                <div className="font-semibold text-slate-900">CI Programme Brochure</div>
                <div className="text-sm text-slate-500">Detailed overview of the initiative</div>
              </div>
              <Download className="w-5 h-5 text-slate-400 ml-auto" />
            </a>

            <a 
              href="https://customer-assets.emergentagent.com/job_dr-sachdeva-ci/artifacts/rlyelspf_CI%20letter.pdf"
              target="_blank"
              rel="noopener noreferrer"
              className="flex items-center gap-4 p-6 bg-white rounded-xl shadow-sm hover:shadow-md transition-shadow"
            >
              <div className="w-12 h-12 rounded-lg bg-sky-100 flex items-center justify-center flex-shrink-0">
                <FileText className="w-6 h-6 text-sky-600" />
              </div>
              <div>
                <div className="font-semibold text-slate-900">CSR Partnership Letter</div>
                <div className="text-sm text-slate-500">Official partnership proposal</div>
              </div>
              <Download className="w-5 h-5 text-slate-400 ml-auto" />
            </a>
          </div>
        </div>
      </section>

      {/* CTA Section */}
      <section className="bg-rose-600 py-16 md:py-24">
        <div className="container-width text-center">
          <Heart className="w-16 h-16 text-white/80 mx-auto mb-6" />
          <h2 className="text-3xl md:text-4xl font-bold text-white mb-6">
            Ready to Change a Child's Life?
          </h2>
          <p className="text-lg text-rose-100 max-w-2xl mx-auto mb-8">
            Contact us to discuss CSR partnership opportunities. Every contribution helps 
            a deaf child hear, speak, and live a normal life.
          </p>
          <div className="flex flex-wrap justify-center gap-4">
            <a href="mailto:entdrsachdeva25@gmail.com?subject=CSR%20Partnership%20Enquiry">
              <Button size="lg" className="bg-white text-rose-600 hover:bg-rose-50 font-bold h-14 px-8">
                <Mail className="w-5 h-5 mr-2" />
                Email Us
              </Button>
            </a>
            <a href="tel:+919910317286">
              <Button size="lg" variant="outline" className="border-white text-white hover:bg-white/10 h-14 px-8">
                <Phone className="w-5 h-5 mr-2" />
                Call: 9910317286
              </Button>
            </a>
          </div>

          <div className="mt-12 pt-8 border-t border-rose-500">
            <p className="text-rose-200 text-sm">
              All donations qualify for tax benefits under Section 80G of the Income Tax Act
            </p>
          </div>
        </div>
      </section>
    </div>
  );
}
